import { Controller } from "@hotwired/stimulus"

/**
 * The properties body for a placed subscription device.
 *
 * Renders state and sends intent, like every other body here. Two rules it
 * shares with the barrier controller and which are easy to erode:
 *
 *  - **No user-visible string is written here.** Every label and every sentence
 *    is rendered by the server; this fills values and toggles `hidden`. A
 *    sentence in a Stimulus file is a sentence the ten locale files cannot
 *    reach. The two words that depend on a value — "Awaiting QR", and a face's
 *    own name when its aisle has none — are carried on the cell that needs
 *    them and read back from there.
 *  - **No design-system class is named here.** Sections show and hide with
 *    `hidden`; what they look like belongs to the components.
 *
 * What it no longer does is leave the installation rows alone. They used to
 * carry a server-rendered "At installation" and keep it for ever, because the
 * seam answered seven hardcoded nulls — so a device an engineer HAD fitted read
 * exactly like one nobody had touched. The facts arrive now, joined onto the
 * state by the canvas controller, and each section hides itself while it has
 * none of them.
 */
export default class extends Controller {
  static targets = [
    "field",
    "section",
    "unattached",
    "detachRow",
    "removeRow",
    "detachBlocked",
    "stateDot",
    "stateLabel",
    "stateHint",
    "stateWords",
    "assetLink",
    "sensorLink",
  ]

  /**
   * The device the canvas has selected, and everything known about it.
   *
   * `null` means what is selected is not a device — the dispatcher hides this
   * whole body then, so there is nothing to render.
   */
  render(event) {
    const state = event.detail?.state
    if (!state) {
      // Nothing selected, or what is selected is not a device. Forgetting the
      // id matters: without this, Release would still carry the LAST device
      // this panel showed and release one the user cannot even see.
      this.deviceId = null
      return
    }

    this.deviceId = state.id

    // A device fixed to nothing can never be paired. Both of these are about
    // that one fact: the warning states it, and the release button has nothing
    // to release.
    const attached = state.host !== null
    this.#toggle(this.unattachedTargets, !attached)
    // ATTACHED AND STILL RELEASABLE, not merely attached.
    //
    // It was `host !== null`, so a device held to its leg showed a button that
    // `detach` refuses — press it and nothing happens. A control that ignores
    // you is worse than no control, so the held case gets the sentence
    // instead: it says where the unit is taken off.
    this.#toggle(this.detachRowTargets, attached && state.releasable)
    // Removable whenever no unit is paired to its leg — fixed to nothing too.
    this.#toggle(this.removeRowTargets, state.releaseBlockedBy === null)

    const blocked = attached && !state.releasable
    this.#toggle(this.detachBlockedTargets, blocked)
    for (const reason of this.element.querySelectorAll(
      "[data-device-blocked]",
    )) {
      reason.hidden =
        !blocked || reason.dataset.deviceBlocked !== state.releaseBlockedBy
    }

    const facts = event.detail?.facts ?? {}
    // A SmartCap stands on a barrier's POST (CONEK-8441): it has no aisle,
    // and its asset is a post.
    const onPost = state.kind === "smartcap"
    this.#renderState(state, facts)
    this.#renderFacts(facts, { onPost })
    this.#renderLinks(event.detail?.links ?? {})
    for (const word of this.element.querySelectorAll(
      "[data-device-link-word]",
    )) {
      word.hidden = (word.dataset.deviceLinkWord === "post") !== onPost
    }
  }

  /**
   * The header: one dot, one word, one sentence about what happens next.
   *
   * Where the panel used to open with two headed sections of blanks. The state
   * itself is resolved by the seam, from the same `deviceStateOf` the canvas
   * paints with — this only renders it.
   */
  #renderState(state, facts = {}) {
    const name = state.state ?? "pending"

    if (this.hasStateDotTarget) this.stateDotTarget.dataset.state = name

    const words = this.hasStateWordsTarget ? this.stateWordsTarget.dataset : {}
    if (this.hasStateLabelTarget) {
      this.stateLabelTarget.textContent = words[`label${cap(name)}`] ?? ""
    }

    // ONE STATE, TWO SITUATIONS.
    //
    // `offline` says "the gateway hears nothing", which is true of a device
    // that spoke and went quiet AND of one that has never spoken at all — and
    // those are different things to go and do. The first has broken. The second
    // has not started: it is what every leg of a migrated fleet looks like
    // before its gateway is pointed at us, and telling somebody their whole
    // warehouse is down would be the wrong sentence on the wrong day.
    //
    // The absence of a last-seen moment is what separates them. The dot stays
    // grey for both; only the sentence changes.
    //
    // "Has broken" is only true because the server's `offline` is: a legacy
    // RackEye's gateway forwards pings only on change, so for one of those
    // `offline` means the gateway reported it not alive (or has itself been
    // gone two hours), never merely that it has been quiet (CONEK-7546).
    // A SmartCap stands on a barrier's POST, not a leg: where its sentence
    // names the thing it is fixed to, it has its own (`hintSmartcap…`).
    const own =
      state.kind === "smartcap" ? words[`hintSmartcap${cap(name)}`] : null
    const hint =
      name === "offline" && !facts.lastSeen
        ? words.hintNeverSeen
        : (own ?? words[`hint${cap(name)}`])

    if (this.hasStateHintTarget) {
      this.stateHintTarget.textContent = hint ?? ""
    }
  }

  /**
   * Every row, then every section that turned out to be empty.
   *
   * One flat map in, because "does this section know anything yet" has to be
   * one question with one answer — and the answer decides whether a reader
   * meets a fact or a column of dashes. A device that has just been drawn knows
   * nothing about a unit or its telemetry, which is most devices for most of
   * their lives.
   */
  #renderFacts(facts, { onPost = false } = {}) {
    const known = new Set()

    for (const el of this.fieldTargets) {
      const key = el.dataset.deviceField
      // A post has no aisle: the row goes, label and all, rather than a dash.
      const absent = onPost && key === "aisle"
      el.hidden = absent
      const label = el.previousElementSibling
      if (label?.tagName === "DT") label.hidden = absent
      if (absent) continue

      const value = this.#valueFor(key, facts)

      el.textContent = value ?? "—"
      if (value) known.add(key)
    }

    for (const section of this.sectionTargets) {
      const fields = (section.dataset.fields ?? "").split(" ").filter(Boolean)
      section.hidden = !fields.some((field) => known.has(field))
    }
  }

  /**
   * One row's value, and the three that are a word rather than a fact.
   *
   * **The QR code.** A leg that IS an asset and has no code yet is not missing
   * data: the code is the reference on a sticker an installer puts on the
   * upright and scans, so every confirmed leg reads this way until somebody
   * stands in front of the rack. The word is on the cell, from the server.
   *
   * **The aisle.** Empty until somebody names it — the server numbers it
   * `Aisle N` within its zone on confirmation, counting what that zone already
   * holds, which the canvas cannot know and must not guess. The face's own
   * word stands in, and it too is on the cell.
   *
   * **What it is fixed to.** A run with no name of its own — most migrated
   * ones — reads as "Racking run", never as its type key.
   */
  #valueFor(key, facts) {
    if (key === "scanCode" && !facts.scanCode) {
      return facts.awaitingQr ? this.#word("scanCode", "awaiting") : null
    }
    if (key === "aisle" && !facts.aisle) {
      return facts.aisleSide ? this.#word("aisle", facts.aisleSide) : null
    }
    if (key === "host" && !facts.host) {
      return facts.hostUnnamed ? this.#word("host", "unnamed") : null
    }
    // Whether the unit is reporting, and whether its battery is low — the two
    // facts an `alert` hides in the state header, which names one word.
    if (key === "connection") {
      return facts.connection
        ? this.#word("connection", facts.connection)
        : null
    }
    if (key === "lowBattery") {
      return facts.lowBattery ? this.#word("lowBattery", "low") : null
    }
    return facts[key] ?? null
  }

  #word(field, name) {
    const cell = this.fieldTargets.find(
      (el) => el.dataset.deviceField === field,
    )
    return cell?.dataset[name] ?? null
  }

  /**
   * The two pages this device has, when it has them.
   *
   * The `hidden` ATTRIBUTE is what has to move, and the trap this app has hit
   * three times is writing only the inline `display`. Tailwind's preflight
   * ships `[hidden]:where(:not([hidden=until-found])){display:none!important}`
   * — measured in the built stylesheet — so an element that still carries the
   * attribute cannot be revealed by a style, inline or otherwise. Both are
   * written here so they can never disagree.
   */
  #renderLinks(links) {
    this.#setLink(this.assetLinkTargets, links.asset ?? null)
    this.#setLink(this.sensorLinkTargets, links.sensor ?? null)
  }

  #setLink(targets, href) {
    for (const el of targets) {
      el.hidden = href === null
      el.style.display = href === null ? "none" : ""
      if (href !== null) el.href = href
    }
  }

  detach() {
    // The id travels with the message. The bridge has no other way to know
    // which device the panel is about, and asking the seam again would be a
    // second answer to a question this controller already holds.
    if (!this.deviceId) return
    this.dispatch("detach", {
      prefix: "device",
      detail: { deviceId: this.deviceId },
      bubbles: true,
    })
  }

  remove() {
    // The device is the canvas's selection — this panel only ever shows one —
    // so the canvas deletes it through the one funnel every delete takes,
    // gate and undo included.
    if (!this.deviceId) return
    this.dispatch("remove", {
      prefix: "device",
      detail: { deviceId: this.deviceId },
      bubbles: true,
    })
  }

  // ───────────────────────────────────────────────────────────────────────────

  #toggle(targets, shown) {
    for (const el of targets) el.hidden = !shown
  }
}

/** `unattached` → `Unattached`, to reach a `data-label-*` key through dataset. */
function cap(word) {
  return word.charAt(0).toUpperCase() + word.slice(1)
}
